import React from "react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import HeroSection from "./HeroSection";
import fabricationHero from "../assets/Brochure.jpg"; // Adjust path as per your project

const Brochure = () => {
  const brochureContents = [
    {
      title: "Company Overview",
      description: "A detailed introduction to who we are and what we do.",
    },
    {
      title: "Industrial Shed Solutions",
      description: "Design options, features, material quality, and application areas.",
    },
    {
      title: "Fabrication Services",
      description: "Range of steel fabrication works including beams, trusses, frames, and structural parts.",
    },
    {
      title: "Industrial Structures",
      description: "High-quality steel structures for manufacturing, processing, and storage units.",
    },
    {
      title: "Prefabricated Buildings",
      description: "Portable, durable, and cost-efficient prefab offices, cabins, and shelters.",
    },
    {
      title: "Shed Fabrication Services",
      description: "Complete shed planning, layout, fabrication, and installation.",
    },
  ];

  const benefits = [
    "Helps you understand our quality standards",
    "Gives clarity on project processes",
    "Includes technical details and material specifications",
    "Helps in budgeting and decision-making",
    "Useful for architects, builders, and contractors",
  ];

  // PDF path from public folder
  const brochurePdfPath = "/vasavi-structures.pdf";

  return (
    <Layout>
      <SEOHead
        title="Download Brochure | Vasavi Structures"
        description="Download the Vasavi Structures brochure for industrial sheds, PEB structures, steel fabrication and prefab buildings — full specifications and details."
        keywords="industrial shed brochure, PEB brochure, steel fabrication brochure, prefab building brochure, Vasavi Structures PDF"
      />

      <main>
        <HeroSection
          title="Download Our Brochure"
          subtitle="Everything You Need to Know"
          description="Get a complete understanding of our products, services, capabilities, and project expertise in one comprehensive document."
          backgroundImage={fabricationHero}
        />

        {/* Brochure Content Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* What's Inside */}
              <div className="animate-slide-in-left">
                <h2 className="font-montserrat text-3xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
                  What You Will Find <span className="text-secondary">Inside</span>
                </h2>
                <div className="space-y-4">
                  {brochureContents.map((item, index) => (
                    <div
                      key={item.title}
                      className={`p-4 bg-card rounded-lg shadow-md card-hover stagger-${index + 1}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-montserrat font-bold text-foreground text-2xl sm:text-lg">
                            {item.title}
                          </h3>
                          <p className="text-muted-foreground text-2xl sm:text-sm">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download Card */}
              <div className="animate-slide-in-right">
                <div className="bg-primary rounded-2xl p-6 md:p-8 text-center sticky top-24">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                    <svg
                      className="w-8 h-8 md:w-10 md:h-10 text-primary-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-3xl md:text-2xl font-bold text-primary-foreground mb-3 md:mb-4">
                    Vasavi Structures Brochure
                  </h3>
                  <p className="text-primary-foreground/80 text-2xl md:text-base mb-4 md:mb-6">
                    Complete guide to our industrial construction services, capabilities, and quality standards.
                  </p>
                  
                  {/* Download Button - Opens PDF in new tab */}
                  <a
                    href={brochurePdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-secondary text-secondary-foreground font-medium py-3 px-6 rounded-lg hover:bg-secondary/90 transition text-1xl md:text-base mb-3"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View Brochure Online
                  </a>

                  {/* Download Button */}
                  <a
                    href={brochurePdfPath}
                    download="vasavi-structure-brochure.pdf"
                    className="inline-flex items-center justify-center gap-2 w-full bg-primary-foreground text-primary font-medium py-3 px-6 rounded-lg hover:bg-primary-foreground/90 transition text-1xl md:text-base"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download PDF Brochure
                  </a>
                  
                  <p className="text-primary-foreground/60 text-1xl md:text-sm mt-3 md:mt-4">
                    PDF • 2.5 MB • Updated 2024
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Download */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-montserrat text-3xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
                Why Download Our <span className="text-primary">Brochure?</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 text-left">
                {benefits.map((benefit, index) => (
                  <div
                    key={benefit}
                    className={`flex items-center gap-3 p-4 bg-card rounded-lg animate-slide-up stagger-${index + 1}`}
                    style={{ animationFillMode: "both" }}
                  >
                    <svg
                      className="w-5 h-5 md:w-6 md:h-6 text-secondary flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-foreground text-2xl md:text-base">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-foreground mb-4">
              Stay Informed. Make the Right Decision.
            </h2>
            <p className="text-secondary-foreground/90 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              Our brochure is regularly updated to include our latest services and completed projects. Download it now to explore how Vasavi Structures can support your next project.
            </p>
            
            {/* CTA Button */}
            <a
              href={brochurePdfPath}
              download="vasavi-structure-brochure.pdf"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold py-4 px-8 rounded-lg hover:bg-primary/90 transition text-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download Brochure Now
            </a>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Brochure;